import { OrderResponseData, RequestQuery, ResponseData } from "./base"
import { User } from "./user";

export const ORDER_STATE = {
  STORNO: -1,
  IN_PROGRESS: 0,
  TO_SENT: 1,
  SENT: 10,
}

export interface Order {
  uuid: string;
  state: number;
  code: string | null;
  createdAt: string;
  loading: OrderLoading | null;
  unloading: OrderUnloading | null;
  goods: OrderGoods[] | null;
  calculation: OrderCalculation | null;
  createdBy: User | null;
}

export interface GetOrdersRequestQuery extends RequestQuery {
  fCode?: string;
  fReference?: string;
  fLoading?: string;
  fUnloading?: string;
  fCreatedBy?: string;
  state?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  loadingDateFrom?: string;
  loadingDateTo?: string;
}

export interface GetOrdersFilters extends Omit<GetOrdersRequestQuery, "limit" | "offset"> { }

export interface OrdersResponseData extends ResponseData {
  readonly results: {
    orderList: Order[];
    totalCount: number;
  };
}

export interface GetOrderResponseData extends ResponseData {
  readonly results: {
    orderDetail: Order;
  };
}

export interface CreateOrderResponseData extends ResponseData {
  readonly results: {
    orderCreate: {
      uuid: string;
    };
  };
}

export interface OrderFinishPayload {
  send: boolean;
}

export interface OrderFinishResponseData extends ResponseData {
  readonly results: {
    orderDetail: Order;
    labelUrl: string;
  };
}

export interface OrderBulkFinishPayload {
  orders: string[];
}

export interface OrderBulkFinishResponseData extends ResponseData {
  readonly results: {
    labelUrl: string;
  };
}

export interface OrderLabelResponseData extends OrderBulkFinishResponseData { };

export interface OrderDocsPayload {
  orders: string[];
  A4PrintStartPosition: null | 1 | 2 | 3 | 4;
  sendEmail: boolean;
}

export interface OrderDocsResponseData extends ResponseData {
  readonly results: {
    label: string;
    list: string;
  }
}

export interface OrderCopyResponseData extends ResponseData {
  readonly results: {
    orderCopy: {
      uuid: string;
    };
  };
}

export interface OrderOrderTemplateCreateResponseData extends ResponseData {
  readonly results: {
    orderTemplateCreate: {
      uuid: string;
    }
  }
}

// --- loading -------------------------------------------------------------------------------------
export interface OrderLoading {
  name: string;
  street: string;
  streetDetails: string | null;
  zip: string;
  city: string;
  country: string;
  person: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  notifyPhone: boolean;
  reference: string | null;
  note: string | null;
  tailgate: boolean;
  addressBookUuid: string | null;
}

export interface OrderLoadingExt extends OrderLoading {
  useAsLoadingDefaultAddress: boolean;
}

export interface UpdateOrderLoadingPaylod {
  loading: OrderLoadingExt;
}

export interface UpdateOrderLoadingResponseData extends OrderResponseData<OrderLoading> {
  readonly results: {
    orderDetail: Order;
  };
}

export interface LoadingTerm {
  term: string;
  label: string;
}

export interface GetLoadingTermsRequestQuery {
  countryCode: string | undefined;
  date: string;
}

export interface LoadingTermsResponseData extends ResponseData {
  readonly results: {
    loadingTermList: LoadingTerm[];
  };
}

// --- unloading -----------------------------------------------------------------------------------
export interface OrderUnloading {
  name: string;
  street: string;
  streetDetails: string | null;
  zip: string;
  city: string;
  country: string;
  person: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  notifyPhone: boolean;
  notifySMS: boolean;
  notifyEmail: boolean;
  reference: string | null;
  note: string | null;
  tailgate: boolean;
  vatId: string | null;
  in: string | null;
  addressBookUuid: string | null;
}

export interface UpdateOrderUnloadingPaylod {
  unloading: OrderUnloading;
}

export interface UpdateOrderUnloadingResponseData extends OrderResponseData<OrderUnloading> {
  readonly results: {
    orderDetail: Order;
  };
}

export interface UnloadingTerm {
  term: string;
  label: string;
}

export interface GetUnloadingTermsRequestQuery {
  unloadingCountryCode?: string;
  loadingCountryCode?: string;
  loadingDate?: string;
}

export interface UnloadingTermsResponseData extends ResponseData {
  readonly results: {
    unloadingTermList: UnloadingTerm[];
  };
}

export interface UnloadingTime {
  time: string;
  label: string;
}

export interface GetUnloadingTimesRequestQuery {
  unloadingDate?: string;
  unloadingCountryCode?: string;
  unloadingZIP?: string;
  loadingCountryCode?: string;
}

export interface UnloadingTimesResponseData extends ResponseData {
  readonly results: {
    unloadingTimeList: UnloadingTime[];
  };
}

// --- goods ---------------------------------------------------------------------------------------
export interface OrderGoods {
  content: string;
  pieces: number;
  weight: number;
  packing: string;
  signification: string | null;
  length: number;
  width: number;
  height: number;
  adr: OrderGoodsADR[];
}

export interface OrderGoodsADR {
  codeUN: number;
  idAdr: number;
  pieces: number;
  brutto: number;
  netto: number;
  technicalName: string;
  technicalNameEN: string | null;
  description: string;
}

export interface UpdateOrderGoodsPaylod {
  goods: OrderGoods[];
}

export interface UpdateOrderGoodsResponseData extends OrderResponseData<OrderGoods> {
  readonly results: {
    orderDetail: Order;
  };
}

export interface UNCode {
  idAdr: number;
  code: string;
  description: string;
}

export interface GetSearchUNCodesRequestQuery {
  code: string;
  search: boolean;
}

export interface SearchUNCodesResponseData extends ResponseData {
  readonly results: {
    UNCodeList: UNCode[];
  };
}

export interface GoodsPacking {
  label: string;
  value: string;
}

export interface GetGoodsPackingsRequestQuery {
  unloadingCountryCode?: string;
  loadingCountryCode?: string;
}

export interface GetOrderGoodsPackingsResponseData extends OrderResponseData<GoodsPacking> {
  readonly results: {
    packings: GoodsPacking[];
  };
}

// --- calculation ---------------------------------------------------------------------------------
export interface OrderCalculation {
  reference: string | null,
  deliveryNote: boolean,
  nonStackable: boolean,
  top: boolean,
  payer: boolean,
  cod: OrderCalculationCod,
  note: string | null;
  loadingDate: string | null;
  loadingTime: string | null;
  unloadingDate: string | null;
  unloadingTime: string | null;
  unloadingIn: string | null;
  unloadingVatId: string | null;
}

export interface OrderCalculationCod {
  amount?: number | null;
  vs?: number | null;
  bankAccount?: string;
}

export interface UpdateOrderCalculationPaylod {
  calculation: OrderCalculation;
}

export interface UpdateOrderCalculationResponseData extends OrderResponseData<OrderCalculation> {
  readonly results: {
    orderDetail: Order;
  };
}

// --- summary -------------------------------------------------------------------------------------
export interface GetOrderSummaryResponseData extends ResponseData {
  readonly results: {
    orderSummary: OrderSummary;
  };
}

export interface OrderSummaryAdditionalFees {
  name: string;
  value: number;
}

export interface OrderSummary {
  additionalFees: OrderSummaryAdditionalFees[];
  calculation: string | null;
  goods: string | null;
  information: string | null;
  isOrderValid: boolean;
  loading: string | null;
  price: number;
  priceInfo: string | null;
  priceVat: number;
  unloading: string | null;
}

export interface OrderImportCSVPayload {
  file: string;
}

export interface OrderImportCSVRowResponse {
  code: string;
  line: number;
  texts: OrderImportCSVRowTextResponse[];
}

export interface OrderImportCSVRowTextResponse {
  text: string;
  errors?: OrderImportCSVRowTextErrorResponse[];
}

export interface OrderImportCSVRowTextErrorResponse {
  text: string;
}

export interface OrderImportCSVResponse extends ResponseData {
  readonly results: {
    rows: OrderImportCSVRowResponse[];
  }
}
